import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import PhotoSection from "./PhotoSection";
import TrashIcon from "./TrashIcon";
import "./CheckinForm.css";
import "./RoomForm.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const CONDITIONS = [
  { value: "good", label: "Good" },
  { value: "insufficient", label: "Insufficient" },
  { value: "poor", label: "Poor" },
];

const SAVE_DEBOUNCE_MS = 800;

const formatTime = (date) =>
  date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

const RoomForm = () => {
  const { cfid, roomId } = useParams();
  const navigate = useNavigate();

  const { sendRequest: getCheckinRequest, isLoading, error } = useHttpNoAuth();
  const {
    sendRequest: saveRoomRequest,
    isLoading: isSaving,
    error: saveError,
  } = useHttpNoAuth();
  const {
    sendRequest: uploadPhotosRequest,
    isLoading: isUploading,
    error: uploadError,
  } = useHttpNoAuth();
  const {
    sendRequest: deletePhotoRequest,
    isLoading: isDeletingPhoto,
    error: deletePhotoError,
  } = useHttpNoAuth();
  const {
    sendRequest: deleteRoomRequest,
    isLoading: isDeleting,
    error: deleteError,
  } = useHttpNoAuth();

  const [address, setAddress] = useState("");
  // Postal code and city read as one place, the way the check-in header
  // writes them.
  const [place, setPlace] = useState("");
  const [room, setRoom] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Signed off, the room is part of the record — everything here only reads.
  const [isSignedOff, setIsSignedOff] = useState(false);
  // The same page serves both handovers — every label follows the type the API
  // sends, and anything that is not a check-out is a check-in.
  const [isCheckout, setIsCheckout] = useState(false);
  const typeLabel = isCheckout ? "check-out" : "check-in";

  const [name, setName] = useState("");
  const [condition, setCondition] = useState(null);
  const [comments, setComments] = useState("");
  const [hasDamage, setHasDamage] = useState(false);
  const [damageComments, setDamageComments] = useState("");

  const [savedAt, setSavedAt] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  // How many thumbs to hold open while the upload is in flight, and which of
  // the two grids they belong to.
  const [pendingUpload, setPendingUpload] = useState(null);

  // What the server currently holds — nothing is sent while the form matches it.
  const baselineRef = useRef(null);
  // Which grid the last upload and the last delete came from, so a failure is
  // reported under the buttons that were pressed. The state change that shows
  // the error re-renders on its own, so a ref is enough.
  const uploadTargetRef = useRef(false);
  const deleteTargetRef = useRef(false);

  const loadRoom = useCallback((onLoaded) => {
    if (!cfid || !roomId) return;
    getCheckinRequest(
      { url: `${BACKEND_URL}/welocate/checkins/${cfid}`, method: "GET" },
      (data) => {
        onLoaded?.();
        const found = (data?.rooms ?? []).find(
          (item) => String(item.id) === String(roomId)
        );

        setAddress(
          [data?.street, data?.number, data?.floor].filter(Boolean).join(" ")
        );
        setPlace([data?.postal_code, data?.city].filter(Boolean).join(" "));
        setIsSignedOff(Boolean(data?.is_signed_off));
        // Set before the early return — the "not found" state names it too.
        setIsCheckout(data?.type === "checkout");

        if (!found) {
          setNotFound(true);
          return;
        }

        setNotFound(false);
        setRoom(found);
        setName(found.name ?? "");
        setCondition(found.condition ?? null);
        setComments(found.comments ?? "");
        setHasDamage(Boolean(found.has_damage));
        setDamageComments(found.damage_comments ?? "");
        baselineRef.current = {
          name: found.name ?? "",
          condition: found.condition ?? null,
          comments: found.comments ?? "",
          hasDamage: Boolean(found.has_damage),
          damageComments: found.damage_comments ?? "",
        };
      }
    );
  }, [cfid, roomId, getCheckinRequest]);

  useEffect(() => {
    loadRoom();
  }, [loadRoom]);

  // Recomputed every render: the baseline lives in a ref and moves on each save.
  const base = baselineRef.current;
  const trimmedName = name.trim();
  // A blank name would leave the room with nothing to call it, so nothing is
  // sent while the field is empty — blur puts the stored name back.
  const isDirty = Boolean(
    base &&
      trimmedName &&
      (base.name !== trimmedName ||
        base.condition !== condition ||
        base.comments !== comments ||
        base.hasDamage !== hasDamage ||
        base.damageComments !== damageComments)
  );

  const saveRoom = useCallback(() => {
    const sent = {
      name: name.trim(),
      condition,
      comments,
      hasDamage,
      damageComments,
    };
    return saveRoomRequest(
      {
        url: `${BACKEND_URL}/welocate/rooms/${roomId}`,
        method: "PATCH",
        body: {
          name: sent.name,
          condition: sent.condition,
          comments: sent.comments,
          has_damage: sent.hasDamage,
          damage_comments: sent.damageComments,
        },
      },
      () => {
        baselineRef.current = sent;
        setSavedAt(new Date());
      }
    );
  }, [
    name,
    condition,
    comments,
    hasDamage,
    damageComments,
    roomId,
    saveRoomRequest,
  ]);

  // Autosave: the design has no save button for the fields, only a "done" exit.
  useEffect(() => {
    if (!isDirty || isSignedOff) return undefined;
    const timer = setTimeout(saveRoom, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isDirty, isSignedOff, saveRoom]);

  // Follows the field as it is typed, so the title and the labels below it
  // rename together.
  const roomName = trimmedName || room?.name || "Room";
  // Both kinds hang off the room; the flag is what keeps them in their own grid.
  const allPhotos = Array.isArray(room?.photos) ? room.photos : [];
  const photos = allPhotos.filter((photo) => !photo.is_damage_closeup_photo);
  const damagePhotos = allPhotos.filter((photo) => photo.is_damage_closeup_photo);

  const uploadPhotos = (fileList, isDamageCloseup = false) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0 || isSignedOff) return;

    const formData = new FormData();
    formData.append("checkin_id", cfid);
    formData.append("is_damage_closeup_photo", isDamageCloseup ? "true" : "false");
    files.forEach((file) => formData.append("files", file));

    uploadTargetRef.current = isDamageCloseup;
    setPendingUpload({ count: files.length, isDamageCloseup });
    uploadPhotosRequest(
      {
        url: `${BACKEND_URL}/welocate/rooms/${roomId}/photos`,
        method: "POST",
        body: formData,
        isFileUpload: true,
      },
      () => {
        setSavedAt(new Date());
        // The skeletons stay up over the refetch, so they hand straight over
        // to the real thumbs instead of blinking out in between.
        loadRoom(() => setPendingUpload(null));
      }
    );
  };

  // The hook never rejects, so a failed upload — or a failed refetch after a
  // good one — has to clear the skeletons here.
  useEffect(() => {
    if (uploadError || error) setPendingUpload(null);
  }, [uploadError, error]);

  const pendingCountFor = (isDamageCloseup) =>
    pendingUpload && pendingUpload.isDamageCloseup === isDamageCloseup
      ? pendingUpload.count
      : 0;

  // Each failure belongs under the grid it came from.
  const photoErrorsFor = (isDamageCloseup) =>
    [
      !isUploading &&
        uploadTargetRef.current === isDamageCloseup &&
        uploadError,
      !isDeletingPhoto &&
        deleteTargetRef.current === isDamageCloseup &&
        deletePhotoError,
    ].filter(Boolean);

  const deletePhoto = (photoId, isDamageCloseup = false) => {
    if (isDeletingPhoto || isSignedOff) return;
    deleteTargetRef.current = isDamageCloseup;
    setDeletingPhotoId(photoId);
    deletePhotoRequest(
      { url: `${BACKEND_URL}/welocate/photos/${photoId}`, method: "DELETE" },
      () => {
        setDeletingPhotoId(null);
        // Drop the thumb straight away — the room holds no other photo state.
        setRoom((current) =>
          current
            ? {
                ...current,
                photos: (current.photos ?? []).filter(
                  (photo) => photo.id !== photoId
                ),
              }
            : current
        );
        setSavedAt(new Date());
      }
    );
  };

  // The hook never rejects, so a failed delete has to release the thumb here.
  useEffect(() => {
    if (deletePhotoError) setDeletingPhotoId(null);
  }, [deletePhotoError]);

  const deleteRoom = () => {
    if (isDeleting || isSignedOff) return;
    deleteRoomRequest(
      { url: `${BACKEND_URL}/welocate/rooms/${roomId}`, method: "DELETE" },
      () => navigate(`/checkin/${cfid}`)
    );
  };

  const finish = async () => {
    if (isDirty) await saveRoom();
    navigate(`/checkin/${cfid}`);
  };

  const isBusy = isSaving || isUploading;
  const hasFailed = !isBusy && Boolean(saveError || uploadError);

  // Edits are only in the browser until the debounce fires and the PATCH lands,
  // so both ways out stay shut until then. A failed save reopens them — the error
  // is on screen and stranding the user on the page helps nobody.
  const isSavePending = (isDirty || isSaving) && !saveError;

  return (
    <div className="cf-page">
      <header className="cf-topbar">
        <button
          type="button"
          className="cf-back"
          aria-label={isSavePending ? "Saving — please wait" : "Back"}
          aria-busy={isSavePending}
          disabled={isSavePending}
          onClick={() => navigate(`/checkin/${cfid}`)}
        >
          {isSavePending ? <span className="cf-spinner" /> : <ChevronLeftIcon />}
        </button>

        <div className="cf-topbar-titles">
          <h1 className="cf-topbar-title">{room ? roomName : "Room"}</h1>
          {/* A record missing either part keeps what it has on its own,
              rather than trailing a separator with nothing after it. */}
          <p className="cf-topbar-sub">
            {[address, place].filter(Boolean).join(" · ")}
          </p>
        </div>

        {(isBusy || hasFailed || savedAt) && (
          <span
            className={`cf-saved${hasFailed ? " rf-saved--error" : ""}`}
            role="status"
          >
            {isBusy && "Saving…"}
            {hasFailed && "Not saved"}
            {!isBusy && !hasFailed && (
              <>
                <CheckCircleOutlineIcon />
                Saved <span className="cf-saved-time">{formatTime(savedAt)}</span>
              </>
            )}
          </span>
        )}
      </header>

      {isLoading && !room && <p className="cf-state">Loading room…</p>}

      {!isLoading && error && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">{error}</p>
          <button type="button" className="cf-state-retry" onClick={loadRoom}>
            Try again
          </button>
        </div>
      )}

      {!isLoading && !error && notFound && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">
            This room is no longer part of the {typeLabel}.
          </p>
          <button
            type="button"
            className="cf-state-retry"
            onClick={() => navigate(`/checkin/${cfid}`)}
          >
            Back to {typeLabel}
          </button>
        </div>
      )}

      {room && (
        <main className="cf-main">
          {isSignedOff && (
            <p className="cf-locked" role="status">
              <LockOutlinedIcon />
              This {typeLabel} has been signed off. Nothing here can be changed.
            </p>
          )}

          <section className="cf-card rf-block">
            <label className="rf-label" htmlFor="rf-name">
              Name
            </label>
            <input
              id="rf-name"
              className="rf-name"
              type="text"
              placeholder="Room name"
              value={name}
              disabled={isSignedOff}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => {
                // Leaving the field empty keeps the stored name rather than
                // dropping the room's only label.
                if (!trimmedName) setName(baselineRef.current?.name ?? "");
              }}
            />

            <h2 className="rf-label">Condition</h2>
            <div
              className="rf-choices rf-choices--rating"
              role="group"
              aria-label="Condition"
            >
              {CONDITIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={condition === option.value}
                  className={`rf-choice${
                    condition === option.value
                      ? ` rf-choice--on rf-choice--${option.value}`
                      : ""
                  }`}
                  disabled={isSignedOff}
                  onClick={() =>
                    setCondition((current) =>
                      current === option.value ? null : option.value
                    )
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>

            <h2 className="rf-label">Notes</h2>
            <textarea
              className="rf-notes"
              placeholder={`State of the ${roomName.toLowerCase()}…`}
              value={comments}
              disabled={isSignedOff}
              onChange={(event) => setComments(event.target.value)}
            />
          </section>

          <section className="cf-card rf-block">
            <PhotoSection
              title={`${roomName.toUpperCase()} — PHOTOS`}
              cameraLabel="Take photos"
              photos={photos}
              pendingCount={pendingCountFor(false)}
              alt={roomName}
              deleteLabel={`Delete photo of ${roomName}`}
              isUploading={isUploading}
              onPick={(files) => uploadPhotos(files, false)}
              onDelete={deletePhoto}
              deletingPhotoId={deletingPhotoId}
              isDeletingPhoto={isDeletingPhoto}
              isLocked={isSignedOff}
              errors={photoErrorsFor(false)}
            />
          </section>

          <div className={`rf-damages-box${hasDamage ? " rf-damages-box--on" : ""}`}>
            <label className={`rf-damages${isSignedOff ? " rf-damages--locked" : ""}`}>
              <input
                type="checkbox"
                className="rf-checkbox"
                checked={hasDamage}
                disabled={isSignedOff}
                onChange={(event) => {
                  setHasDamage(event.target.checked);
                  // The field disappears with the tick, so its text goes too.
                  if (!event.target.checked) setDamageComments("");
                }}
              />
              <span className="rf-damages-body">
                <span className="rf-damages-title">Damages</span>
                <span className="rf-damages-sub">
                  Close-ups and notes stay in this room's section
                </span>
              </span>
            </label>

            {hasDamage && (
              <div className="rf-damages-detail">
                <label className="rf-label" htmlFor="rf-damage-comments">
                  Damage notes
                </label>
                <textarea
                  id="rf-damage-comments"
                  className="rf-notes rf-notes--damage"
                  placeholder="e.g. 12 cm scratch on the left worktop edge."
                  value={damageComments}
                  disabled={isSignedOff}
                  onChange={(event) => setDamageComments(event.target.value)}
                />
                {!isSignedOff && <p className="rf-help">What, where, how bad.</p>}

                <PhotoSection
                  title="DAMAGE CLOSE-UPS"
                  cameraLabel="Close-up"
                  photos={damagePhotos}
                  pendingCount={pendingCountFor(true)}
                  alt={`${roomName} damage`}
                  deleteLabel={`Delete damage close-up of ${roomName}`}
                  isUploading={isUploading}
                  onPick={(files) => uploadPhotos(files, true)}
                  onDelete={(photoId) => deletePhoto(photoId, true)}
                  deletingPhotoId={deletingPhotoId}
                  isDeletingPhoto={isDeletingPhoto}
                  isLocked={isSignedOff}
                  errors={photoErrorsFor(true)}
                />
              </div>
            )}
          </div>

          {/* A signed-off room is part of the record, so it cannot be taken
              out of the report any more. */}
          {!isSignedOff && (
            <>
              {confirmingDelete ? (
                <div className="rf-delete-confirm">
                  <button
                    type="button"
                    className="rf-delete rf-delete--solid"
                    disabled={isDeleting}
                    onClick={deleteRoom}
                  >
                    {isDeleting ? "Deleting…" : `Yes, delete ${roomName}`}
                  </button>
                  <button
                    type="button"
                    className="rf-delete-cancel"
                    disabled={isDeleting}
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="rf-delete"
                  onClick={() => setConfirmingDelete(true)}
                >
                  <TrashIcon />
                  Delete this room
                </button>
              )}

              <p className="rf-delete-help">
                Deleting removes the room from the report along with its notes
                and photos.
              </p>

              {!isDeleting && deleteError && (
                <p className="rf-error" role="alert">
                  {deleteError}
                </p>
              )}
            </>
          )}

          {!isSaving && saveError && (
            <p className="rf-error" role="alert">
              {saveError}
            </p>
          )}
        </main>
      )}

      {room && (
        <footer className="cf-actions">
          <div className="cf-actions-inner">
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              aria-busy={isSavePending}
              disabled={isSavePending}
              onClick={finish}
            >
              {isSavePending ? (
                <span className="cf-spinner rf-btn-spinner" />
              ) : (
                <CheckIcon />
              )}
              {isSignedOff ? `Back to ${typeLabel}` : `Done with ${roomName}`}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default RoomForm;
